import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useBi } from '@components/Hooks/useBi';
import { copyOfExploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import { useCallback } from 'react';
import {
  agencyProfileBriefGeneralActionSrc80Evid332,
  agencyProfileBriefLoadedSrc80Evid335,
  agencyProfileSubmitBriefSrc80Evid336,
  projectModalGenericClickSrc80Evid1022,
  projectModalPageViewSrc80Evid1023,
} from '@wix/bi-logger-partners-data/v2';
import { Leaves, PROFILE_PAGE_TYPES } from '@common/types';

export const AGENCY_BRIEF_ACTION_TYPES = {
  typeClientName: 'type client name',
  typeEmail: 'type email',
  subject: 'subject',
  cancel: 'exit/cancel',
  send: 'send',
  clickSubmit: 'click submit',
} as const;
export type IAgencyBriefActionTypes = Leaves<typeof AGENCY_BRIEF_ACTION_TYPES>;

export const convertDataToString = (data: any) => {
  return data && typeof data === 'object'
    ? JSON.stringify(data)
    : data?.toString();
};

export const useProfilePageBi = () => {
  const logger = useBi();
  const { sessionId } = useBiSessionContext();
  const { entry, origin, profilePageType, partnerProfile } =
    useProfileContext();
  const partnerProfileId = partnerProfile?.id;
  const isInMarketplace = profilePageType === PROFILE_PAGE_TYPES.marketplace;

  const logTemplateClick = useCallback(
    (templateId?: string, clickType?: string) => {
      logger.report(
        copyOfExploreResultsPageGenericClick({
          clickType,
          sessionId,
          origin,
          entry,
          exploreVersion: 'V2',
          data: JSON.stringify({ templateId }),
        }),
      );
    },
    [logger, sessionId, origin, entry],
  );

  const logProfilePageClick = useCallback(
    (clickType: string, data?: any) => {
      const stringifiedClickData = convertDataToString(data);

      logger.report(
        copyOfExploreResultsPageGenericClick({
          clickType: clickType,
          sessionId,
          origin,
          entry,
          inMarketplace: isInMarketplace,
          exploreVersion: 'V2',
          data: stringifiedClickData ?? '',
        }),
      );
    },
    [logger, sessionId, origin, entry],
  );

  const logProjectModalClick = useCallback(
    (clickType: string, data?: string) => {
      logger.report(
        projectModalGenericClickSrc80Evid1022({
          clickType,
          data,
        }),
      );
    },
    [logger],
  );

  const logProjectClick = useCallback(
    (clickType: string, data?: string, entry?: string) => {
      logger.report(
        projectModalPageViewSrc80Evid1023({
          clickType,
          data,
          entry,
        }),
      );
    },
    [logger, entry],
  );

  const logAgencyBriefAction = useCallback(
    ({ actionType }: { actionType: IAgencyBriefActionTypes }) => {
      logger.report(
        agencyProfileBriefGeneralActionSrc80Evid332({
          action_type: actionType,
        }),
      );
    },
    [logger],
  );

  const logAgencyBriefLoad = useCallback(() => {
    logger.report(
      agencyProfileBriefLoadedSrc80Evid335({
        entry,
      }),
    );
  }, [logger]);

  const logAgencyBriefSubmit = useCallback(
    (formData: Record<string, string | undefined>) => {
      const stringifiedFormData = convertDataToString(formData);
      logger.report(
        agencyProfileSubmitBriefSrc80Evid336({
          entry,
          partnersAccountId: partnerProfileId,
          data: stringifiedFormData,
          action_type: AGENCY_BRIEF_ACTION_TYPES.clickSubmit,
        }),
      );
    },
    [logger],
  );

  return {
    logTemplateClick,
    logProfilePageClick,
    logProjectModalClick,
    logProjectClick,
    isInMarketplace,
    logAgencyBriefAction,
    logAgencyBriefLoad,
    logAgencyBriefSubmit,
  };
};
